import {
  ChevronDown,
  Database as DatabaseIcon,
  MessageSquare,
  RefreshCw,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { cn } from '../../../lib/utils.js';
import { TeamsDropdown, TEAM_ALL } from '../../common/components/TeamsDropdown.js';
import { FOLDER_ALL, FOLDER_UNCATEGORIZED } from '../../scenario/components/ScenarioPageHeader.js';
import {
  presetToDateRange,
  rollupByScenarioMetric,
  useEffectiveMainMetricId,
  useKpiMetrics,
  useKpiValues,
} from '../../kpi/hooks.js';
import { asKpiMetric } from '../../kpi/types.js';
import {
  PresentationDateRangeDropdown,
  type PresentationDateRangePreset,
} from '../../presentation/components/PresentationDateRangeDropdown.js';
import { FusionRequestSheet } from '../../scenario/components/FusionRequestSheet.js';
import { useScenariosList } from '../../scenario/hooks.js';
import type { ScenarioRow } from '../../scenario/types.js';
import { CostSavingCard } from '../components/CostSavingCard.js';
import { MainMetricByDayChart } from '../components/MainMetricByDayChart.js';
import { MainMetricCard } from '../components/MainMetricCard.js';
import { ScenarioKpisByAmountTable } from '../components/ScenarioKpisByAmountTable.js';
import { ScenariosByValueTable } from '../components/ScenariosByValueTable.js';
import { StatCard } from '../components/StatCard.js';
import { ValueByDayChart } from '../components/ValueByDayChart.js';
import { useDashboardSummary, useRefreshDashboard } from '../hooks.js';
import { aggregate, formatNumber } from '../utils.js';

/**
 * Daily Dashboard. Mirrors the legacy
 * `wf-audit/src/slices/dashboard/components/dashboard/Provider.tsx`
 * composition (`sec_125`):
 *
 *   [ All | Active | Inactive ]      [ Date Range ▾ ]      [ Team ▾ ]
 *   ┌─────────────┬──────────┬──────────┬─────────────┐
 *   │ Cost Saving │ Executns │ Operatns │ Scenario Rn │
 *   └─────────────┴──────────┴──────────┴─────────────┘
 *   ┌────────────────────────────┬────────────────────────────┐
 *   │ Scenarios By Value         │ Scenario KPIs By Amount    │
 *   └────────────────────────────┴────────────────────────────┘
 *
 * Some legacy widgets (Cost Saving, Scenario KPIs By Amount) need
 * data the engine doesn't currently emit. We render the visual
 * structure with `0` / "No data" placeholders so the layout is in
 * place; the values light up once the rollup endpoint ships.
 *
 * Filter row state:
 *   - status pills (All / Active / Inactive) → narrows the
 *     `Scenarios By Value` row set + the Operations card count.
 *   - date range preset → bubbled state for the future KPI-rollup
 *     query; doesn't filter the local set today.
 *   - Teams dropdown (URL-pinned `?team=`) → narrows the row set
 *     by `teamFusionId`.
 */
type StatusFilter = 'all' | 'active' | 'inactive';

export function DashboardPage(): JSX.Element {
  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    error: summaryError,
  } = useDashboardSummary();
  const {
    data: scenarios,
    isLoading: scenariosLoading,
    isFetching: scenariosFetching,
  } = useScenariosList();
  const refreshAll = useRefreshDashboard();
  const refreshing = summaryFetching || scenariosFetching;

  const [params, setParams] = useSearchParams();
  const teamFilter = params.get('team') ?? TEAM_ALL;
  // Folder filter — URL-pinned at `?folder=` with the same sentinel
  // pattern as the Scenarios / Value Metrics pages. `__all__` keeps
  // every row, `__uncategorized__` matches rows without a folder.
  const folder = params.get('folder') ?? FOLDER_ALL;
  const [status, setStatus] = useState<StatusFilter>('all');
  const [dateRange, setDateRange] = useState<PresentationDateRangePreset>('last-30-days');
  const [fusionRequestOpen, setFusionRequestOpen] = useState(false);

  // KPI rollup — the date-range preset drives the `useKpiValues`
  // query that backs the Cost Saving card, Scenarios By Value's
  // Hours / Value columns, and the Scenario KPIs By Amount table.
  const { from, to } = useMemo(() => presetToDateRange(dateRange), [dateRange]);
  const { data: kpiValues = [] } = useKpiValues({ from, to, aggregationType: 'day' });
  const rollup = useMemo(() => rollupByScenarioMetric(kpiValues), [kpiValues]);
  const mainMetricId = useEffectiveMainMetricId();
  const { data: metricRows = [] } = useKpiMetrics();
  const mainMetric = useMemo(() => {
    if (!mainMetricId) return null;
    return metricRows.map(asKpiMetric).find((m) => String(m.id) === mainMetricId) ?? null;
  }, [metricRows, mainMetricId]);

  function setTeam(next: string): void {
    const ps = new URLSearchParams(params);
    if (next === TEAM_ALL) ps.delete('team');
    else ps.set('team', next);
    setParams(ps);
  }

  function setFolder(next: string): void {
    const ps = new URLSearchParams(params);
    if (next === FOLDER_ALL) ps.delete('folder');
    else ps.set('folder', next);
    setParams(ps);
  }

  // `useScenariosList` types its data loosely as `Row[]`; the rest
  // of the dashboard expects the narrowed `ScenarioRow` shape that
  // the scenarios slice already exposes. Cast once at the boundary.
  const rows = (scenarios ?? []) as unknown as ReadonlyArray<ScenarioRow>;

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (status === 'active' && r.status !== 'On') return false;
      if (status === 'inactive' && r.status === 'On') return false;
      if (teamFilter !== TEAM_ALL && (r.teamFusionId ?? '') !== teamFilter) return false;
      if (folder === FOLDER_UNCATEGORIZED) {
        if (r.folderName && r.folderName.length > 0) return false;
      } else if (folder !== FOLDER_ALL) {
        if (r.folderName !== folder) return false;
      }
      return true;
    });
  }, [rows, status, teamFilter, folder]);

  // Folder dropdown options — distinct folder names actually present
  // on the license's scenarios. Sorted alphabetically for stable
  // ordering across renders.
  const folderOptions = useMemo<ReadonlyArray<string>>(() => {
    const names = new Set<string>();
    for (const r of rows) {
      if (r.folderName && r.folderName.length > 0) names.add(r.folderName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const folderLabel =
    folder === FOLDER_ALL
      ? 'All scenarios'
      : folder === FOLDER_UNCATEGORIZED
        ? 'Uncategorized'
        : folder;

  const counts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    const teamFiltered =
      teamFilter === TEAM_ALL ? rows : rows.filter((r) => (r.teamFusionId ?? '') === teamFilter);
    for (const r of teamFiltered) {
      if (r.status === 'On') active += 1;
      else inactive += 1;
    }
    return { all: teamFiltered.length, active, inactive };
  }, [rows, teamFilter]);

  const agg = useMemo(
    () => aggregate(filteredRows as unknown as ReadonlyArray<Record<string, unknown>>),
    [filteredRows],
  );

  // Cost Saving totals + Scenario KPIs By Amount data — both fall
  // off the same per-(scenario, metric) rollup. Money Saved =
  // value, Hours Saved = quantity (legacy convention — see
  // `kpi-value.ts` schema).
  //
  // `mainMetricId` is the license's preferred "headline" metric, but
  // it can be null (license not set up yet) or it can point to a
  // metric that has no recorded values in the active date range. In
  // either case we fall back to summing across **all** metrics so
  // the surface shows something useful (demo mode in particular
  // ships fixture data with metric ids that don't match a real
  // license's `mainMetricId`).
  const costSavingTotals = useMemo(() => {
    let totalValue = 0;
    let totalQuantity = 0;
    const filteredIds = new Set(filteredRows.map((r) => r.fusionId).filter(Boolean));
    for (const [scenarioId, byMetric] of rollup) {
      if (!filteredIds.has(scenarioId)) continue;
      if (mainMetricId && byMetric.has(mainMetricId)) {
        const totals = byMetric.get(mainMetricId)!;
        totalValue += totals.value;
        totalQuantity += totals.quantity;
      } else {
        for (const totals of byMetric.values()) {
          totalValue += totals.value;
          totalQuantity += totals.quantity;
        }
      }
    }
    return { value: totalValue, quantity: totalQuantity };
  }, [rollup, mainMetricId, filteredRows]);

  // Main-metric totals — sum specifically the configured main metric
  // across the filtered scenario set. Distinct from `costSavingTotals`
  // (which aggregates across all metrics) so the 4th dashboard card
  // can show the main metric's headline Quantity + Amount.
  const mainMetricTotals = useMemo(() => {
    if (!mainMetricId) return { value: 0, quantity: 0 };
    let totalValue = 0;
    let totalQuantity = 0;
    const filteredIds = new Set(filteredRows.map((r) => r.fusionId).filter(Boolean));
    for (const [scenarioId, byMetric] of rollup) {
      if (!filteredIds.has(scenarioId)) continue;
      const totals = byMetric.get(mainMetricId);
      if (!totals) continue;
      totalValue += totals.value;
      totalQuantity += totals.quantity;
    }
    return { value: totalValue, quantity: totalQuantity };
  }, [rollup, mainMetricId, filteredRows]);

  const topByAmount = useMemo(() => {
    const filteredIds = new Set(filteredRows.map((r) => r.fusionId).filter(Boolean));
    const nameById = new Map<string, string>();
    for (const r of filteredRows) {
      if (r.fusionId && r.name) nameById.set(r.fusionId, r.name);
    }
    const out: Array<{ fusionId: string; scenarioName: string; amount: number }> = [];
    for (const [scenarioId, byMetric] of rollup) {
      if (!filteredIds.has(scenarioId)) continue;
      let amount = 0;
      if (mainMetricId && byMetric.has(mainMetricId)) {
        amount = byMetric.get(mainMetricId)!.value;
      } else {
        for (const totals of byMetric.values()) amount += totals.value;
      }
      if (amount <= 0) continue;
      out.push({
        fusionId: scenarioId,
        scenarioName: nameById.get(scenarioId) ?? `Scenario ${scenarioId}`,
        amount,
      });
    }
    return out.sort((a, b) => b.amount - a.amount).slice(0, 10);
  }, [rollup, mainMetricId, filteredRows]);

  const loading = summaryLoading && !summary;

  return (
    <div className="relative z-10 flex h-full flex-col">
      {/* Header — title + segmented page filters + Refresh +
          Fusion Request. Matches the legacy `sec_125` placement
          (status pills + date range + team selector top-right). */}
      <header className="flex flex-wrap items-center gap-3 px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Daily Dashboard</h1>
        <span className="text-foreground/30">|</span>
        {/* Folder dropdown — same trigger + sentinels as the
            Scenarios / Value Metrics pages. URL-backed at
            `?folder=`. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-base text-foreground/80 transition-colors hover:border-foreground/15 hover:bg-foreground/5 hover:text-foreground"
            >
              <span>{folderLabel}</span>
              <ChevronDown className="h-4 w-4 text-foreground/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[60vh] min-w-[220px] overflow-y-auto">
            <DropdownMenuLabel>Folder</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_ALL}
              onCheckedChange={() => setFolder(FOLDER_ALL)}
            >
              All scenarios
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_UNCATEGORIZED}
              onCheckedChange={() => setFolder(FOLDER_UNCATEGORIZED)}
            >
              Uncategorized
            </DropdownMenuCheckboxItem>
            {folderOptions.length > 0 ? <DropdownMenuSeparator /> : null}
            {folderOptions.map((name) => (
              <DropdownMenuCheckboxItem
                key={name}
                checked={folder === name}
                onCheckedChange={() => setFolder(name)}
              >
                {name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex-1" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={refreshAll}
          disabled={refreshing}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
        >
          <RefreshCw className={cn('mr-1.5 h-4 w-4', refreshing && 'animate-spin')} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFusionRequestOpen(true)}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
        >
          <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Fusion Request
        </Button>
      </header>

      {/* Filter row — status pills (left) + date range (centered
          against the table below) + Teams dropdown (right).
          3-col grid `[1fr][auto][1fr]` puts the date range in the
          middle column so it's geometrically centered regardless
          of the side cluster widths — matches the Scenarios list
          + Value Metrics layout. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-foreground/10 bg-foreground/[0.03] px-6 py-3">
        <div className="flex min-w-0 items-center">
          <div className="inline-flex rounded-md border border-foreground/15 bg-foreground/[0.04] p-1">
            {(['all', 'active', 'inactive'] as const).map((k) => {
              const sel = status === k;
              const count =
                k === 'all' ? counts.all : k === 'active' ? counts.active : counts.inactive;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setStatus(k)}
                  className={cn(
                    'flex items-center gap-2 rounded px-3 py-1 text-sm capitalize transition-colors',
                    sel
                      ? 'bg-foreground/15 text-foreground'
                      : 'text-foreground/70 hover:text-foreground',
                  )}
                >
                  <span>{k}</span>
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-xs',
                      sel
                        ? 'bg-foreground/20 text-foreground'
                        : 'bg-foreground/10 text-foreground/60',
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <PresentationDateRangeDropdown value={dateRange} onChange={setDateRange} />
        <div className="flex min-w-0 items-center justify-end">
          <TeamsDropdown team={teamFilter} onTeamChange={setTeam} />
        </div>
      </div>

      {summaryError ? (
        <div className="mx-6 my-4 rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
          Could not load dashboard summary: {summaryError.message}
        </div>
      ) : null}

      {/* Top 4-card row — legacy parity (`sec_065`/`sec_070`):
          Cost Saving | Executions | Operations | {Main Metric}.
          The 4th tile is metric-driven (icon + Quantity + Amount)
          rather than a generic Scenario Runs counter. */}
      <div className="grid grid-cols-1 gap-3 px-6 py-3 sm:grid-cols-2 xl:grid-cols-4">
        <CostSavingCard
          moneySaved={costSavingTotals.value}
          hoursSaved={costSavingTotals.quantity}
        />
        <StatCard
          icon={DatabaseIcon}
          label="Executions"
          tint="emerald"
          value={
            scenariosLoading
              ? '—'
              : `${formatNumber(agg.scenariosLastRunWithin24h)} / ${formatNumber(agg.totalScenarios)}`
          }
          caption="Data Transfer (scenarios run in last 24h)"
        />
        <StatCard
          icon={Wrench}
          label="Operations"
          tint="orange"
          value={scenariosLoading ? '—' : formatNumber(agg.totalOperations)}
          caption="Total (last run)"
        />
        <MainMetricCard
          metricName={mainMetric?.displayName ?? null}
          metricIcon={mainMetric?.icon ?? null}
          metricColor={mainMetric?.color ?? null}
          quantity={mainMetricTotals.quantity}
          amount={mainMetricTotals.value}
        />
      </div>

      {/* Bottom 2-table row — Scenarios By Value + Scenario KPIs By
          Amount, side-by-side. Matches `sec_055` / `sec_060`. */}
      <div className="grid grid-cols-1 gap-3 px-6 py-3 lg:grid-cols-2">
        <ScenariosByValueTable rows={filteredRows} rollup={rollup} mainMetricId={mainMetricId} />
        <ScenarioKpisByAmountTable rows={topByAmount} />
      </div>

      {/* By-day chart tiles below the tables — `sec_060` / `sec_065`.
          Value by Day stacks the top-3 scenarios + Others; the right
          tile shows the main metric's per-day quantity. */}
      <div className="grid grid-cols-1 gap-3 px-6 pb-6 lg:grid-cols-2">
        <ValueByDayChart items={kpiValues} rows={filteredRows} mainMetricId={mainMetricId} />
        <MainMetricByDayChart
          items={kpiValues}
          rows={filteredRows}
          mainMetricId={mainMetricId}
          metricName={mainMetric?.displayName ?? null}
          metricColor={mainMetric?.color ?? null}
        />
      </div>

      <FusionRequestSheet open={fusionRequestOpen} onOpenChange={setFusionRequestOpen} />
    </div>
  );
}
